/** Rehacer el Round Robin al cambiar quién está en un grupo (Eduardo
 * 2026-10-13, torneo 71: «si hay cambio de grupo entonces desprogramar los
 * juegos en automático»). El backend arma los textos en español; aquí solo
 * se juntan para la pantalla y la confirmación. */

import type { GroupStrayMatch, MembershipChangePreview, RrRebuildSummary } from "./api";
import { formatDate } from "./dateFormat";

/** Aviso después de guardar: «Se rehicieron los partidos de … y quedaron sin
 * programar: vuelve a ponerles fecha, hora y cancha.» más los avisos de los
 * grupos que ya tenían partidos jugados. null si no cambió ningún partido. */
export function rrRebuildNotice(summary: RrRebuildSummary | null | undefined): string | null {
  if (!summary) return null;
  const parts = [summary.message, ...(summary.warnings ?? [])].filter(
    (part): part is string => typeof part === "string" && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(" ") : null;
}

/** Varias operaciones seguidas (asignar a varios jugadores): un solo aviso
 * con todos los grupos que se rehicieron. */
export function mergedRrRebuildNotice(summaries: Array<RrRebuildSummary | null | undefined>): string | null {
  // Cada aviso del servidor ya dice qué se canceló, qué quedó sin programar y
  // qué se agregó: se juntan sin repetir.
  const parts: string[] = [];
  for (const summary of summaries) {
    for (const part of [summary?.message, ...(summary?.warnings ?? [])]) {
      const text = typeof part === "string" ? part.trim() : "";
      if (text && !parts.includes(text)) parts.push(text);
    }
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

/** Avisos (sin repetir) de varias vistas previas para una sola confirmación. */
export function previewWarnings(previews: Array<MembershipChangePreview | null | undefined>): string[] {
  const out: string[] = [];
  for (const preview of previews) {
    const text = preview?.warning?.trim() || preview?.notice?.trim() || "";
    if (text && !out.includes(text)) out.push(text);
  }
  return out;
}

/** La vista previa es solo un aviso: si falla o tarda más de `ms`, se sigue
 * sin ella (el servidor vuelve a decidir al guardar). */
export async function settleWithin<T>(promise: Promise<T>, ms = 8000): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Junta el mensaje de éxito de siempre con el aviso del rehacer. */
export function withRrRebuildNotice(base: string, summary: RrRebuildSummary | null | undefined): string {
  const notice = rrRebuildNotice(summary);
  return notice ? `${base} ${notice}` : base;
}

/** Texto de la confirmación: la pregunta de siempre y, si el cambio deja sin
 * programar partidos que ya tenían fecha, hora o cancha, ese aviso (o, sin
 * horarios que perder, que los partidos se rehacen). */
export function membershipConfirmText(
  question: string,
  preview: MembershipChangePreview | null | undefined,
  extra?: string | null,
): string {
  const lines = [question];
  const warning = preview?.warning?.trim() || preview?.notice?.trim() || "";
  if (warning) lines.push(warning);
  if (extra && extra.trim()) lines.push(extra.trim());
  return lines.join("\n\n");
}

/** ¿El cambio deja sin programar algo que tenía horario? (la confirmación se
 * pinta como acción delicada). */
export function membershipLosesSchedule(preview: MembershipChangePreview | null | undefined): boolean {
  return (preview?.lost_schedule ?? 0) > 0;
}

/** «Hay 2 partidos de jugadores que ya no están en este grupo» / «Hay 1
 * partido repetido en este grupo» / las dos cosas. */
export function strayMatchesHeadline(matches: GroupStrayMatch[]): string {
  const ghosts = matches.filter((match) => match.kind !== "duplicate").length;
  const duplicates = matches.length - ghosts;
  const parts: string[] = [];
  if (ghosts > 0) {
    parts.push(
      ghosts === 1
        ? "Hay 1 partido de un jugador que ya no está en este grupo"
        : `Hay ${ghosts} partidos de jugadores que ya no están en este grupo`,
    );
  }
  if (duplicates > 0) {
    const text = duplicates === 1 ? "1 partido repetido" : `${duplicates} partidos repetidos`;
    parts.push(ghosts > 0 ? `y ${text}` : `Hay ${text} en este grupo`);
  }
  return `${parts.join(" ")}.`;
}

function slotPieces(date: string | null | undefined, time: string | null | undefined, court: string | null | undefined): string[] {
  return [date ? formatDate(date, "") : null, time, court].filter(
    (piece): piece is string => typeof piece === "string" && piece.trim().length > 0,
  );
}

function slotText(match: GroupStrayMatch): string {
  const pieces = slotPieces(match.scheduled_date, match.scheduled_time, match.court);
  return pieces.length > 0 ? ` (${pieces.join(" · ")})` : "";
}

/** Cuál se queda de un repetido: por su horario, que es lo que distingue a
 * dos copias en la misma jornada (torneo 71). */
function keeperText(match: GroupStrayMatch): string {
  const keeps = match.keeps_round_label?.trim();
  const pieces = slotPieces(match.keeps_scheduled_date, match.keeps_scheduled_time, match.keeps_court);
  const when = pieces.length > 0 ? `el que tiene horario (${pieces.join(" · ")})` : "el que está sin horario";
  return keeps ? `se queda ${when}, de la ${keeps}` : `se queda ${when}`;
}

/** Un renglón de la lista de la confirmación: «Jornada 1: Alvaro vs Ex
 * (ya no está: Ex)» o «Jornada 1: Alvaro vs Patricio (repetido; se queda el
 * de la Jornada 3)». */
export function strayMatchLine(match: GroupStrayMatch): string {
  const where = match.round_label?.trim() || "Sin jornada";
  const who = `${match.player1.name} vs ${match.player2.name}`;
  if (match.kind === "duplicate") {
    return `${where}: ${who}${slotText(match)} — repetido; ${keeperText(match)}`;
  }
  const gone = match.not_in_group.map((player) => player.name).join(" y ");
  return `${where}: ${who}${slotText(match)} — ${gone ? `ya no está: ${gone}` : "ya no está en el grupo"}`;
}

/** Texto completo de la confirmación de «Quitar esos partidos». */
export function strayMatchesConfirmText(groupName: string, matches: GroupStrayMatch[]): string {
  const total = matches.length;
  const head =
    total === 1
      ? `Se va a cancelar 1 partido del ${groupName}:`
      : `Se van a cancelar ${total} partidos del ${groupName}:`;
  const list = matches.map((match) => `• ${strayMatchLine(match)}`).join("\n");
  return `${head}\n${list}\n\nLos partidos jugados no se tocan. Queda anotado en el historial de cada partido.`;
}
